import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Box,
    Typography,
    Tab,
    LinearProgress,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { socketEventEmitter } from "socket";
import { useProject } from "context/ProjectContext";
import { useNotification } from "context/NotificationContext";
import ProjectService from "services/ProjectService";
import PageService from "services/PageService";
import Header from "components/Header";
import Sidebar from "components/Sidebar";
import BreadcrumbsNav from "components/BreadcrumbsNav";
import StatsSummary from "components/StatsSummary";
import VideoPlayer from "components/VideoPlayer";
import RunSteps from "components/testRun/RunSteps";
import TestRunActions from "components/testRun/TestRunActions";
import TestRunAssertion from "components/testRun/TestRunAssertion";
import DeleteTestRunConfirmationModal from "components/modals/DeleteTestRunConfirmationModal";
import {
    getTestRunCycleLabel,
    getTestRunVersionLabel,
} from "utils/formatTestRunContext";
import { formatDate } from "utils/formatDate";
import PageLink from "components/common/PageLink";
import TraceTab from "./components/TraceTab";
import { shouldShowTestGenerationDebug } from "../../utils/addExternalScripts";
import {
    formatTokenCount,
    formatUsdCost,
    getDiscoveryDebugSummary,
} from "../../utils/discoveryDebug";

const TestRun = () => {
    const { id } = useParams();
    const { selectedProject, setSelectedProject } = useProject();
    const videoRef = useRef(null);
    const navigate = useNavigate();
    const showNotification = useNotification();

    const [testRun, setTestRun] = useState(null);
    const [testCase, setTestCase] = useState(null);
    const [activeTabValue, setActiveTabValue] = useState("1");
    const [activeStep, setActiveStep] = useState(null);
    const [videoTime, setVideoTime] = useState(0);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [pageToggler, setPageToggler] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    const [isFeedbackPending, setIsFeedbackPending] = useState(false);
    const debugSummary = getDiscoveryDebugSummary(testRun?.discovery_debug);

    const syncProjectSelection = useCallback(async (projectId) => {
        if (!projectId || selectedProject?.id === projectId) {
            return;
        }

        try {
            const projectData = await ProjectService.getProjectById(projectId);
            setSelectedProject(projectData);
        } catch (error) {
            console.error("Error syncing selected project:", error);
        }
    }, [selectedProject?.id, setSelectedProject]);

    useEffect(() => {
        let isSubscribed = true;

        const getTestCase = async (testCaseId) => {
            try {
                const testCase = await PageService.getTestCaseById(testCaseId);
                if (!isSubscribed) return;
                setTestCase(testCase);
            } catch (error) {
                console.error("Error fetching test case:", error);
            }
        };

        const getData = async () => {
            try {
                const testRunResponse = await PageService.getTestRunById(id);

                await syncProjectSelection(testRunResponse?.projectId);

                if (!isSubscribed) return;
                setTestRun(testRunResponse);
                setActiveStep(testRunResponse?.steps[0]?.id);

                getTestCase(testRunResponse.testCaseId);
            } catch (error) {
                console.error("Error fetching test run:", error);
            }
        };

        getData();

        const handleTaskCompleted = async (data) => {
            if (data.type === "Test Case" && data?.testCaseRunId === id) {
                setPageToggler(!pageToggler);
            }
        };

        const handleTaskFailed = async (data) => {
            if (data.type === "Test Case" && data?.testCaseRunId === id) {
                setPageToggler(!pageToggler);
            }
        };

        socketEventEmitter.on("taskCompleted", handleTaskCompleted);
        socketEventEmitter.on("taskFailed", handleTaskFailed);

        return () => {
            isSubscribed = false;
            socketEventEmitter.off("taskCompleted", handleTaskCompleted);
            socketEventEmitter.off("taskFailed", handleTaskFailed);
        };
        // eslint-disable-next-line
    }, [id, pageToggler, syncProjectSelection]);

    useEffect(() => {
        if (
            testRun &&
            selectedProject?.id &&
            testRun.projectId !== selectedProject.id
        ) {
            navigate(`/pages`);
        }
    }, [testRun?.projectId, selectedProject?.id, navigate]);

    useEffect(() => {
        if (activeTabValue === "1" && videoRef.current) {
            videoRef.current.currentTime = videoTime;
        }
    }, [activeTabValue, videoTime]);

    useEffect(() => {
        (async () => {
            try {
                if (
                    typeof window !== "undefined" &&
                    "TREEGRESS_SHOW_DEBUG" in window
                ) {
                    setShowDebug(Boolean(window.TREEGRESS_SHOW_DEBUG));
                } else {
                    setShowDebug(await shouldShowTestGenerationDebug());
                }
            } catch {
                setShowDebug(false);
            }
        })();
    }, []);

    const statusStyles = {
        PENDING: { backgroundColor: "#F0AD4E", color: "#fff" },
        RUNNING: { backgroundColor: "#4574D2", color: "#fff" },
        COMPLETED: { backgroundColor: "#198754", color: "#fff" },
        FAILED: { backgroundColor: "#D32F2F", color: "#fff" },
        INITIAL_FAILURE: { backgroundColor: "#8E1515", color: "#fff" },
        CANCELLED: { backgroundColor: "#F8DF73", color: "#2D4356" },
    };

    const handleRunTestCase = async () => {
        try {
            const res = await PageService.runTestCase(testRun?.testCaseId);
            navigate(`/test-run/${res[0].testCaseRunId}`);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSeek = (time) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            // videoRef.current.play()
        }
    };

    const handleChangeActiveTab = (event, newValue) => {
        if (activeTabValue === "1" && videoRef.current) {
            setVideoTime(videoRef.current.currentTime);
        }
        setActiveTabValue(newValue);
    };

    const handleOpenDeleteDialog = () => {
        setDeleteOpen(true);
    };

    const handleFeedbackClick = async (event, nextValue) => {
        event?.stopPropagation?.();
        if (!testCase?.id) return;

        try {
            setIsFeedbackPending(true);
            const value = testCase.my_feedback === nextValue ? null : nextValue;
            const result = await PageService.updateTestCaseFeedback(
                testCase.id,
                value
            );
            setTestCase((prev) =>
                prev
                    ? {
                          ...prev,
                          my_feedback: result?.my_feedback ?? null,
                          likes_count: Number(result?.likes_count) || 0,
                          dislikes_count: Number(result?.dislikes_count) || 0,
                      }
                    : prev
            );
        } catch (error) {
            console.error("Error updating test case feedback:", error);
            showNotification("Failed to save feedback", "error");
        } finally {
            setIsFeedbackPending(false);
        }
    };

    return (
        <Box className="testRun rootWrapper">
            <Helmet>
                <title>{`Treegress - ${selectedProject.name} ${testRun?.pageLabel} ${testRun?.name} for ${testRun?.testCaseName}`}</title>
            </Helmet>
            <Header title={testRun?.pageLabel} />
            <Box className="srollContent">
                <Sidebar />
                <Box className="testRunContentContainer">
                    <BreadcrumbsNav
                        pageLabel={testCase?.journey?.id ? "journeyTestRun" : "testRun"}
                        pageName={testRun?.pageLabel}
                        pageId={testRun?.pageId}
                        testCaseName={testRun?.testCaseName}
                        testCaseId={testRun?.testCaseId}
                        journey={testCase?.journey}
                    />
                    <Box className="testRunTopSection">
                        <Box className="testRunTitleContainer">
                            <Box
                                className="testRunBackButton"
                                onClick={() =>
                                    navigate(
                                        `/test-case/${testRun?.testCaseId}`
                                    )
                                }
                            >
                                <ArrowBackIosIcon />
                            </Box>
                            <Typography
                                className="testRunTitle"
                                variant="h6"
                                color="primary"
                            >
                                {testRun?.name} {testRun?.testCaseName}
                            </Typography>
                        </Box>
                        <TestRunActions
                            testCase={testCase}
                            handleRunTestCase={handleRunTestCase}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            handleFeedbackClick={handleFeedbackClick}
                            isFeedbackPending={isFeedbackPending}
                        />
                    </Box>
                    <Box className="testRunMiddleSection">
                        <Box className="testRunInfoContainer">
                            <PageLink link={testRun?.pageUrl} />
                            <Box className="testRunPageDescriptionContainer">
                                <Typography variant="body1" color="primary">
                                    <span className="testRunPageDescriptionLabel">
                                        Description:{" "}
                                    </span>
                                    {testRun?.description}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider />
                        <Box className="testRunStatsContainer">
                            <StatsSummary
                                title="Test Version"
                                value={getTestRunVersionLabel(testRun)}
                                color="primary"
                            />
                            <StatsSummary
                                title="Status"
                                value={
                                    testRun?.status ? (
                                        <span className="testRunStatsSummaryStatusContainer">
                                            <span
                                                className="testRunStatsSummaryStatus"
                                                style={{
                                                    ...statusStyles[
                                                        testRun.status
                                                    ],
                                                }}
                                            >
                                                {testRun?.status === "RUNNING"
                                                    ? "In Progress..."
                                                    : testRun.status ===
                                                      "COMPLETED"
                                                    ? "Passed"
                                                    : testRun.status ===
                                                      "INITIAL_FAILURE"
                                                    ? "Initial failure"
                                                    : testRun.status.toLowerCase()}
                                            </span>
                                        </span>
                                    ) : (
                                        ""
                                    )
                                }
                                color="primary"
                            />
                            <StatsSummary
                                title="Test Cycle"
                                value={getTestRunCycleLabel(testRun)}
                                color="primary"
                            />
                            <StatsSummary
                                title="Created by"
                                value={testRun?.created_by || "-"}
                                color="primary"
                            />
                            <StatsSummary
                                title="Date and Time"
                                value={
                                    testRun?.queued_at
                                        ? formatDate(testRun?.queued_at)
                                        : ""
                                }
                                color="primary"
                            />
                        </Box>
                        <Divider />
                    </Box>
                    {testRun &&
                    testRun?.steps &&
                    (testRun.status === "COMPLETED" ||
                        testRun.status === "FAILED" ||
                        testRun.status === "INITIAL_FAILURE") ? (
                        <Box className="testRunResultsSection">
                            <Box className="testRunResultsSectionTitle">
                                Results
                            </Box>
                            <Box className="testRunResultsSectionContent">
                                <TableContainer
                                    className="testRunResultsTableContainer"
                                    component={Paper}
                                >
                                    <Table className="testRunResultsTable">
                                        <TableHead className="testRunResultsTableHeader">
                                            <TableRow>
                                                <TableCell className="testRunResultsTableHeaderCell stepIndexHeaderCell">
                                                    #
                                                </TableCell>
                                                <TableCell className="testRunResultsTableHeaderCell stepNameHeaderCell">
                                                    Step Name
                                                </TableCell>
                                                <TableCell className="testRunResultsTableHeaderCell stepDataHeaderCell">
                                                    Test Data
                                                </TableCell>
                                                <TableCell className="testRunResultsTableHeaderCell resultHeaderCell"></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <RunSteps
                                                steps={testRun.steps}
                                                activeStep={activeStep}
                                                setActiveStep={setActiveStep}
                                                handleSeek={handleSeek}
                                            />
                                            <TestRunAssertion
                                                testRun={testRun}
                                            />
                                            {testRun.evaluatuion_conclusions ? (
                                                <TableRow>
                                                    <TableCell colSpan={4}>
                                                        <Box className="testRunResultsAIConclusionContainer">
                                                            <Typography
                                                                className="testRunResultsAIConclusionContent"
                                                                variant="body1"
                                                                color="primary"
                                                            >
                                                                <span className="testRunResultsAIConclusionLabel">
                                                                    AI
                                                                    Conclusion:{" "}
                                                                </span>
                                                                <span className="testRunResultsAIConclusionText">
                                                                    {
                                                                        testRun.evaluatuion_conclusions
                                                                    }
                                                                </span>
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ) : null}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box className="testRunResultsContentTabs">
                                    <TabContext value={activeTabValue}>
                                        <Box className="testRunResultsTabsTogglerContainer">
                                            <TabList
                                                className="testRunResultsTabsToggler"
                                                onChange={handleChangeActiveTab}
                                            >
                                                <Tab
                                                    className="testRunResultsTabLabelContainer"
                                                    label={
                                                        <Box
                                                            className="testRunResultsTabLabel"
                                                            sx={{
                                                                color:
                                                                    activeTabValue ===
                                                                    "1"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)",
                                                            }}
                                                        >
                                                            <PlayCircleIcon
                                                                className="testRunResultsTabLabelIcon"
                                                                color={
                                                                    activeTabValue ===
                                                                    "1"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)"
                                                                }
                                                            />
                                                            <span className="testRunResultsTabLabelText">
                                                                VIEW
                                                            </span>
                                                        </Box>
                                                    }
                                                    value="1"
                                                />
                                                <Tab
                                                    className="testRunResultsTabLabelContainer"
                                                    label={
                                                        <Box
                                                            className="testRunResultsTabLabel"
                                                            sx={{
                                                                color:
                                                                    activeTabValue ===
                                                                    "2"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)",
                                                            }}
                                                        >
                                                            <ArticleOutlinedIcon
                                                                className="testRunResultsTabLabelIcon"
                                                                color={
                                                                    activeTabValue ===
                                                                    "2"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)"
                                                                }
                                                            />
                                                            <span className="testRunResultsTabLabelText">
                                                                Trace Report (Logs)
                                                            </span>
                                                        </Box>
                                                    }
                                                    value="2"
                                                />
                                                <Tab
                                                    className="testRunResultsTabLabelContainer"
                                                    label={
                                                        <Box
                                                            className="testRunResultsTabLabel"
                                                            sx={{
                                                                color:
                                                                    activeTabValue ===
                                                                    "4"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)",
                                                            }}
                                                        >
                                                            <ArticleOutlinedIcon
                                                                className="testRunResultsTabLabelIcon"
                                                                color={
                                                                    activeTabValue ===
                                                                    "4"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)"
                                                                }
                                                            />
                                                            <span className="testRunResultsTabLabelText">
                                                                Context
                                                            </span>
                                                        </Box>
                                                    }
                                                    value="4"
                                                />
                                                {showDebug &&
                                                testRun?.discovery_debug
                                                    ?.llm_logs?.length > 0 ? (
                                                    <Tab
                                                        className="testRunResultsTabLabelContainer"
                                                        label={
                                                            <Box
                                                                className="testRunResultsTabLabel"
                                                                sx={{
                                                                    color:
                                                                        activeTabValue ===
                                                                        "3"
                                                                            ? "#4574D2"
                                                                            : "rgba(0, 0, 0, 0.6)",
                                                                }}
                                                            >
                                                                <ArticleOutlinedIcon
                                                                    className="testRunResultsTabLabelIcon"
                                                                    color={
                                                                        activeTabValue ===
                                                                        "3"
                                                                            ? "#4574D2"
                                                                            : "rgba(0, 0, 0, 0.6)"
                                                                    }
                                                                />
                                                                <span className="testRunResultsTabLabelText">
                                                                    LLM I/O
                                                                </span>
                                                            </Box>
                                                        }
                                                        value="3"
                                                    />
                                                ) : null}
                                            </TabList>
                                        </Box>
                                        <TabPanel
                                            value="1"
                                            className="testRunResultsTabContentContainer"
                                        >
                                            <VideoPlayer
                                                ref={videoRef}
                                                videoSrc={testRun?.videoUrl}
                                            />
                                        </TabPanel>
                                        <TabPanel
                                            value="2"
                                            className="testRunResultsTabContentContainer"
                                        >
                                            <TraceTab
                                                traceUrl={
                                                    testRun?.aws_tracing_url
                                                }
                                            />
                                        </TabPanel>
                                        {showDebug &&
                                        testRun?.discovery_debug?.llm_logs
                                            ?.length > 0 ? (
                                            <TabPanel
                                                value="3"
                                                className="testRunResultsTabContentContainer"
                                            >
                                                {debugSummary ? (
                                                    <Box
                                                        sx={{
                                                            mb: 2,
                                                            p: 1.5,
                                                            border: "1px solid #E3E8EF",
                                                            borderRadius: 2,
                                                            backgroundColor: "#F7F9FC",
                                                        }}
                                                    >
                                                        <Typography fontWeight={600}>
                                                            Gemini cost
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Total:{" "}
                                                            {formatUsdCost(
                                                                debugSummary.totalUsd
                                                            )}{" "}
                                                            {debugSummary.currency}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Tokens:{" "}
                                                            {formatTokenCount(
                                                                debugSummary.inputTokens
                                                            )}{" "}
                                                            in /{" "}
                                                            {formatTokenCount(
                                                                debugSummary.outputTokens
                                                            )}{" "}
                                                            out
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Priced calls:{" "}
                                                            {formatTokenCount(
                                                                debugSummary.pricedCalls
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                ) : null}
                                                <TableContainer
                                                    component={Paper}
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>
                                                                    #
                                                                </TableCell>
                                                                <TableCell>
                                                                    Route
                                                                </TableCell>
                                                                <TableCell>
                                                                    Cost (USD)
                                                                </TableCell>
                                                                <TableCell>
                                                                    Request JSON
                                                                </TableCell>
                                                                <TableCell>
                                                                    Response
                                                                    JSON
                                                                </TableCell>
                                                                <TableCell>
                                                                    Timestamp
                                                                </TableCell>
                                                                <TableCell>
                                                                    Image
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {testRun.discovery_debug.llm_logs.map(
                                                                (row, idx) => (
                                                                    <TableRow
                                                                        key={
                                                                            idx
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            {idx +
                                                                                1}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                row.route
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {formatUsdCost(
                                                                                row.llm_cost_usd
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {row.request_json ? (
                                                                                <a
                                                                                    href={
                                                                                        row.request_json
                                                                                    }
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                >
                                                                                    open
                                                                                </a>
                                                                            ) : (
                                                                                "-"
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {row.response_json ? (
                                                                                <a
                                                                                    href={
                                                                                        row.response_json
                                                                                    }
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                >
                                                                                    open
                                                                                </a>
                                                                            ) : (
                                                                                "-"
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {row.response_timestamp
                                                                                ? formatDate(row.response_timestamp)
                                                                                : row.request_timestamp
                                                                                ? formatDate(row.request_timestamp)
                                                                                : ""}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {row.image ? (
                                                                                <a
                                                                                    href={
                                                                                        row.image
                                                                                    }
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                >
                                                                                    image
                                                                                </a>
                                                                            ) : (
                                                                                "-"
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </TabPanel>
                                        ) : null}
                                        <TabPanel
                                            value="4"
                                            className="testRunResultsTabContentContainer"
                                        >
                                            <Box className="testRunResultsLogContainer">
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        Requirements:
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            border: "1px solid rgba(0,0,0,0.12)",
                                                            borderRadius: 1,
                                                            p: 1,
                                                            maxHeight: 200,
                                                            overflow: "auto",
                                                            fontSize: 14,
                                                            whiteSpace: "pre-wrap",
                                                        }}
                                                    >
                                                        {testRun?.requirements ||
                                                            ""}
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        Expected Test Cases:
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            border: "1px solid rgba(0,0,0,0.12)",
                                                            borderRadius: 1,
                                                            p: 1,
                                                            maxHeight: 200,
                                                            overflow: "auto",
                                                            fontSize: 14,
                                                            whiteSpace: "pre-wrap",
                                                        }}
                                                    >
                                                        {testRun?.expected_test_cases ||
                                                            ""}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </TabPanel>
                                    </TabContext>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ mt: 2, width: "100%" }}>
                            <Typography mb={1}>Test case running...</Typography>
                            <LinearProgress color="secondary" />
                        </Box>
                    )}
                </Box>
                <DeleteTestRunConfirmationModal
                    testRun={testRun}
                    deleteOpen={deleteOpen}
                    setDeleteOpen={setDeleteOpen}
                />
            </Box>
        </Box>
    );
};

export default TestRun;
